// Project Publish Page — Data Product Hub owner UI (Phase 1, 2026-05-21)
// Route: /workspace/storage/:projectName/publish
// - List products của project (incl. drafts)
// - Tạo product mới (draft) → form
// - Tạo version mới
// - Publish/Deprecate
// - Quick view notebook URL + Colab open

import { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box, Container, Typography, Button, Card, CardContent, Chip, Stack,
    CircularProgress, Alert, Grid, IconButton, Dialog, DialogTitle, DialogContent,
    DialogActions, TextField, MenuItem, Select, FormControl, InputLabel, Switch,
    FormControlLabel, Tooltip, Tabs, Tab, Link
} from '@mui/material';
import {
    IconPlus, IconRocket, IconArchive, IconEdit, IconBrandGoogle, IconExternalLink,
    IconCloudUpload, IconHistory, IconRefresh
} from '@tabler/icons-react';
import axios from 'axios';

const api = axios.create({
    baseURL: process.env.REACT_APP_API_URL || '/api/v1',
});
api.interceptors.request.use((cfg) => {
    const t = localStorage.getItem('serviceToken') || localStorage.getItem('auth_token');
    if (t) cfg.headers.Authorization = `Bearer ${t}`;
    return cfg;
});

const PRODUCT_TYPES = [
    { value: 'use_case', label: 'Use Case (RAG + data)' },
    { value: 'table', label: 'Table / Dataset' },
    { value: 'model', label: 'Model (inference)' },
    { value: 'notebook', label: 'Notebook (free-form)' },
    { value: 'dashboard', label: 'Dashboard (iframe)' },
];

const STATUS_COLOR = {
    draft: 'default',
    published: 'success',
    deprecated: 'warning',
};

function CreateProductDialog({ open, onClose, projectId, onCreated }) {
    const [form, setForm] = useState({
        name: '', type: 'use_case', description: '', license: 'CC-BY-4.0',
        citation: '', tagsStr: '', requires_colab_pro: false,
        knowledge_collection_id: '', visibility: 'public',
    });
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    const handleSubmit = async () => {
        setErr(null);
        if (!form.name.trim() || form.name.length < 2) {
            setErr('Tên phải có ít nhất 2 ký tự');
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                name: form.name.trim(),
                type: form.type,
                description: form.description.trim() || null,
                license: form.license.trim() || null,
                citation: form.citation.trim() || null,
                tags: form.tagsStr ? form.tagsStr.split(',').map((t) => t.trim()).filter(Boolean) : null,
                requires_colab_pro: form.requires_colab_pro,
                knowledge_collection_id: form.knowledge_collection_id ? parseInt(form.knowledge_collection_id) : null,
                visibility: form.visibility,
            };
            await api.post(`/projects/${projectId}/data-products`, payload);
            onCreated();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message || 'Create failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Tạo Data Product mới</DialogTitle>
            <DialogContent>
                {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Tên" fullWidth required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder="VN30 CEO Overconfidence Panel"
                    />
                    <FormControl fullWidth>
                        <InputLabel>Loại</InputLabel>
                        <Select
                            value={form.type} label="Loại"
                            onChange={(e) => setForm({ ...form, type: e.target.value })}
                        >
                            {PRODUCT_TYPES.map((t) => (
                                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <TextField
                        label="Mô tả" fullWidth multiline rows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        placeholder="Tóm tắt ngắn về data product này..."
                    />
                    <Stack direction="row" spacing={2}>
                        <TextField
                            label="License" sx={{ flex: 1 }}
                            value={form.license}
                            onChange={(e) => setForm({ ...form, license: e.target.value })}
                            placeholder="CC-BY-4.0"
                        />
                        <TextField
                            label="Knowledge Collection ID" sx={{ flex: 1 }}
                            type="number"
                            value={form.knowledge_collection_id}
                            onChange={(e) => setForm({ ...form, knowledge_collection_id: e.target.value })}
                            placeholder="(tùy chọn) ID collection RAG"
                        />
                    </Stack>
                    <TextField
                        label="Citation" fullWidth
                        value={form.citation}
                        onChange={(e) => setForm({ ...form, citation: e.target.value })}
                        placeholder="Author (Year). Title. Publisher."
                    />
                    <TextField
                        label="Tags (CSV)" fullWidth
                        value={form.tagsStr}
                        onChange={(e) => setForm({ ...form, tagsStr: e.target.value })}
                        placeholder="fintech, vn30, nlp"
                    />
                    <Stack direction="row" spacing={2} alignItems="center">
                        <FormControl sx={{ minWidth: 160 }}>
                            <InputLabel>Visibility</InputLabel>
                            <Select
                                value={form.visibility} label="Visibility"
                                onChange={(e) => setForm({ ...form, visibility: e.target.value })}
                            >
                                <MenuItem value="public">Public</MenuItem>
                                <MenuItem value="tenant">Tenant only</MenuItem>
                                <MenuItem value="private">Private</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControlLabel
                            control={<Switch checked={form.requires_colab_pro}
                                onChange={(e) => setForm({ ...form, requires_colab_pro: e.target.checked })} />}
                            label="Yêu cầu Colab Pro (GPU)"
                        />
                    </Stack>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Hủy</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? <CircularProgress size={16} /> : 'Tạo Draft'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function CreateVersionDialog({ open, onClose, slug, onCreated }) {
    const [form, setForm] = useState({
        version: '1.0.0',
        filesText: '[]',
        questionsText: '[]',
        changelog: '',
    });
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState(null);

    const handleSubmit = async () => {
        setErr(null);
        let files, questions;
        try {
            files = form.filesText.trim() ? JSON.parse(form.filesText) : [];
            questions = form.questionsText.trim() ? JSON.parse(form.questionsText) : [];
        } catch (e) {
            setErr('JSON files/questions không hợp lệ: ' + e.message);
            return;
        }
        if (!/^\d+\.\d+\.\d+$/.test(form.version.trim())) {
            setErr('Version phải dạng semver: 1.0.0');
            return;
        }
        setSubmitting(true);
        try {
            await api.post(`/data-products/${slug}/versions`, {
                version: form.version.trim(),
                files, sample_questions: questions,
                changelog: form.changelog.trim() || null,
            });
            onCreated();
            onClose();
        } catch (e) {
            setErr(e?.response?.data?.detail || e?.message || 'Create version failed');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Tạo version mới — {slug}</DialogTitle>
            <DialogContent>
                {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <TextField
                        label="Version (semver)" fullWidth required
                        value={form.version}
                        onChange={(e) => setForm({ ...form, version: e.target.value })}
                        placeholder="1.0.0"
                    />
                    <TextField
                        label="Files (JSON array)" fullWidth multiline rows={4}
                        value={form.filesText}
                        onChange={(e) => setForm({ ...form, filesText: e.target.value })}
                        placeholder='[{"path":"data/gold/panel.csv","label":"Panel","format":"csv"}]'
                        helperText='Mỗi item: {"path","label","format"}. format: csv|parquet'
                    />
                    <TextField
                        label="Sample questions (JSON array, use_case only)" fullWidth multiline rows={3}
                        value={form.questionsText}
                        onChange={(e) => setForm({ ...form, questionsText: e.target.value })}
                        placeholder='["Câu hỏi 1", "Câu hỏi 2"]'
                    />
                    <TextField
                        label="Changelog" fullWidth multiline rows={2}
                        value={form.changelog}
                        onChange={(e) => setForm({ ...form, changelog: e.target.value })}
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Hủy</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
                    {submitting ? <CircularProgress size={16} /> : 'Tạo Version'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function ProjectPublishPage() {
    const { projectName } = useParams();
    const navigate = useNavigate();
    const [projectId, setProjectId] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [createOpen, setCreateOpen] = useState(false);
    const [versionDialog, setVersionDialog] = useState({ open: false, slug: null });

    // Lookup project_id từ project_name
    useEffect(() => {
        api.get('/projects/list').then((r) => {
            const found = (r.data || []).find((p) => p.project_name === projectName);
            if (found) setProjectId(found.id);
            else setError(`Không tìm thấy project "${projectName}" hoặc bạn không có quyền`);
        }).catch((e) => setError(e?.message || 'Cannot load projects'));
    }, [projectName]);

    const reload = useCallback(async () => {
        if (!projectId) return;
        setLoading(true);
        try {
            const r = await api.get(`/projects/${projectId}/data-products`);
            setProducts(r.data || []);
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Load products failed');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => { reload(); }, [reload]);

    const handlePublish = async (slug) => {
        if (!window.confirm(`Publish "${slug}"? Sẽ xuất hiện public catalog.`)) return;
        try {
            await api.post(`/data-products/${slug}/publish`);
            reload();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Publish failed');
        }
    };
    const handleDeprecate = async (slug) => {
        if (!window.confirm(`Deprecate "${slug}"? Sẽ ẩn khỏi catalog (không xóa data).`)) return;
        try {
            await api.post(`/data-products/${slug}/deprecate`);
            reload();
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || 'Deprecate failed');
        }
    };

    const baseUrl = window.location.origin;

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h3" sx={{ fontWeight: 700, mb: 0.5 }}>
                        Publish — Data Product Hub
                    </Typography>
                    <Typography color="text.secondary">
                        Project <strong>{projectName}</strong> · Publish final tables, use cases ra catalog Colab
                    </Typography>
                </Box>
                <Stack direction="row" spacing={1}>
                    <Tooltip title="Reload">
                        <IconButton onClick={reload}><IconRefresh size={18} /></IconButton>
                    </Tooltip>
                    <Button
                        variant="outlined"
                        startIcon={<IconExternalLink size={16} />}
                        href="/catalog"
                        target="_blank"
                    >
                        Mở public catalog
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<IconPlus size={16} />}
                        onClick={() => setCreateOpen(true)}
                        disabled={!projectId}
                    >
                        Tạo Data Product
                    </Button>
                </Stack>
            </Stack>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading && (
                <Box sx={{ textAlign: 'center', py: 4 }}><CircularProgress /></Box>
            )}

            {!loading && products.length === 0 && (
                <Alert severity="info">
                    Project này chưa có Data Product nào. Click "Tạo Data Product" để bắt đầu.
                </Alert>
            )}

            <Grid container spacing={2}>
                {products.map((p) => (
                    <Grid item xs={12} md={6} key={p.id}>
                        <Card sx={{ height: '100%' }}>
                            <CardContent>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <Chip size="small" label={p.type} color="primary" sx={{ height: 20, fontSize: 11 }} />
                                    <Chip size="small" label={p.status} color={STATUS_COLOR[p.status] || 'default'} sx={{ height: 20, fontSize: 11 }} />
                                    <Chip size="small" label={p.visibility} variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                    {p.latest_version && (
                                        <Chip size="small" label={`v${p.latest_version}`} sx={{ height: 20, fontSize: 11 }} />
                                    )}
                                </Stack>

                                <Typography variant="h5" sx={{ fontWeight: 600, mb: 0.5 }}>
                                    {p.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    slug: <code>{p.slug}</code>
                                </Typography>

                                <Stack direction="row" spacing={2} sx={{ mt: 2, mb: 2 }}>
                                    <Typography variant="caption" color="text.secondary">
                                        👁 {p.view_count} views
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        ⬇ {p.download_count} downloads
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                        <IconHistory size={11} style={{ verticalAlign: 'middle' }} /> {p.version_count} versions
                                    </Typography>
                                </Stack>

                                {p.status === 'published' && (
                                    <Box sx={{ p: 1, bgcolor: 'success.50', borderRadius: 1, mb: 2 }}>
                                        <Typography variant="caption" color="success.dark" sx={{ display: 'block' }}>
                                            Catalog URL:{' '}
                                            <Link href={`/catalog`} target="_blank" rel="noopener">
                                                /catalog → {p.slug}
                                            </Link>
                                        </Typography>
                                        <Typography variant="caption" color="success.dark" sx={{ display: 'block', mt: 0.5 }}>
                                            Notebook: <code style={{ fontSize: 10 }}>{baseUrl}/api/v1/data-products/{p.slug}/notebook.ipynb</code>
                                        </Typography>
                                    </Box>
                                )}

                                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                    <Button
                                        size="small" startIcon={<IconCloudUpload size={14} />}
                                        onClick={() => setVersionDialog({ open: true, slug: p.slug })}
                                        variant="outlined"
                                    >
                                        Tạo version
                                    </Button>
                                    {p.status === 'draft' && (
                                        <Button
                                            size="small" startIcon={<IconRocket size={14} />}
                                            onClick={() => handlePublish(p.slug)}
                                            variant="contained" color="success"
                                            disabled={!p.latest_version}
                                        >
                                            Publish
                                        </Button>
                                    )}
                                    {p.status === 'published' && (
                                        <>
                                            <Button
                                                size="small"
                                                startIcon={<IconBrandGoogle size={14} />}
                                                href={`https://colab.research.google.com/import/url?url=${encodeURIComponent(baseUrl + '/api/v1/data-products/' + p.slug + '/notebook.ipynb')}`}
                                                target="_blank"
                                                variant="contained"
                                            >
                                                Open in Colab
                                            </Button>
                                            <Button
                                                size="small" startIcon={<IconArchive size={14} />}
                                                onClick={() => handleDeprecate(p.slug)}
                                                color="warning"
                                            >
                                                Deprecate
                                            </Button>
                                        </>
                                    )}
                                </Stack>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            <CreateProductDialog
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                projectId={projectId}
                onCreated={reload}
            />
            <CreateVersionDialog
                open={versionDialog.open}
                slug={versionDialog.slug}
                onClose={() => setVersionDialog({ open: false, slug: null })}
                onCreated={reload}
            />
        </Container>
    );
}
